/**
 * UserMenu — Fixed top-right user avatar with dropdown menu.
 * Shows on every authenticated page via WithUnifiedChat wrapper.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Avatar, Tag, Typography } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  DashboardOutlined,
  ShopOutlined,
  CrownOutlined,
  WalletOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { checkGet } from '../../api/checkClient';
import { useAuthStore } from '../../stores/authStore';
import { getDisplayRole } from '../../utils/productRole';
import { getUserCapabilities } from '../../utils/accountCapabilities';

const { Text } = Typography;

const roleColor: Record<string, string> = {
  person:     'default',
  enterprise: 'blue',
  buyer:      'gold',
  investor:   'gold',
  admin:      'red',
  operator:   'green',
  dev:        'purple',
};

const UserMenu: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [creditsRemaining, setCreditsRemaining] = useState<number | null>(user?.credits_remaining ?? null);

  if (!user) return null;

  const role = user.role || 'person';
  const displayRole = user.display_role || getDisplayRole(role);
  const displayName = user.email?.split('@')[0] || 'User';
  const caps = getUserCapabilities(user);

  const loadCredits = useCallback(async () => {
    try {
      const balance = await checkGet<{ credits_remaining: number }>('/credits/balance');
      setCreditsRemaining(balance.credits_remaining);
    } catch {
      setCreditsRemaining(user.credits_remaining ?? null);
    }
  }, [user.credits_remaining]);

  useEffect(() => {
    setCreditsRemaining(user.credits_remaining ?? null);
  }, [user.credits_remaining]);

  useEffect(() => {
    void loadCredits();
    const onFocus = () => { void loadCredits(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadCredits]);

  const header: NonNullable<MenuProps['items']>[0] = {
    key: 'header',
    type: 'group',
    label: (
      <div style={{ padding: '4px 0' }}>
        <div><Text strong>{displayName}</Text></div>
        <Tag color={roleColor[role]} style={{ marginTop: 2 }}>{displayRole}</Tag>
      </div>
    ),
  };

  let items: MenuProps['items'];

  if (caps.use_external_menu) {
    // Unified external customer menu: person / enterprise / investor / buyer
    items = [
      header,
      { type: 'divider' },
      {
        key: 'projects',
        icon: <DashboardOutlined />,
        label: 'My Projects',
        onClick: () => navigate('/portfolio'),
      },
      ...(caps.can_browse_marketplace
        ? [{
            key: 'marketplace',
            icon: <ShopOutlined />,
            label: 'Market',
            onClick: () => navigate('/marketplace'),
          }]
        : []),
      {
        key: 'billing',
        icon: <WalletOutlined />,
        label: 'Billing & Plan',
        onClick: () => navigate('/billing'),
      },
      { type: 'divider' },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Logout',
        danger: true,
        onClick: () => { logout(); navigate('/login'); },
      },
    ];
  } else if (role === 'operator') {
    // Operator menu: approval-first, minimal
    items = [
      header,
      { type: 'divider' },
      {
        key: 'my-approvals',
        icon: <CrownOutlined />,
        label: 'My Approvals',
        onClick: () => navigate('/operator/approvals'),
      },
      {
        key: 'my-projects',
        icon: <DashboardOutlined />,
        label: 'My Projects',
        onClick: () => navigate('/portfolio'),
      },
      {
        key: 'my-sales',
        icon: <GiftOutlined />,
        label: 'My Sales',
        onClick: () => navigate('/operator/sales'),
      },
      {
        key: 'marketplace',
        icon: <WalletOutlined />,
        label: 'Market',
        onClick: () => navigate('/marketplace'),
      },
      { type: 'divider' },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Logout',
        danger: true,
        onClick: () => { logout(); navigate('/login'); },
      },
    ];
  } else {
    // Admin / dev menu
    items = [
      header,
      { type: 'divider' },
      {
        key: 'approval-dashboard',
        icon: <CrownOutlined />,
        label: 'Approval Dashboard',
        onClick: () => navigate('/admin'),
      },
      {
        key: 'plant-analytics',
        icon: <DashboardOutlined />,
        label: 'Plant Analytics',
        onClick: () => navigate('/admin/plants'),
      },
      ...(role === 'admin' || role === 'dev'
        ? [{
            key: 'user-admin',
            icon: <CrownOutlined />,
            label: 'User Admin',
            onClick: () => navigate('/admin/users'),
          }]
        : []),
      { type: 'divider' },
      {
        key: 'billing',
        icon: <WalletOutlined />,
        label: 'Billing & Plan',
        onClick: () => navigate('/admin/billing'),
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Logout',
        danger: true,
        onClick: () => { logout(); navigate('/login'); },
      },
    ];
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 12,
        right: 12,
        zIndex: 3000,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      {creditsRemaining != null && (
        <div
          style={{
            color: 'rgba(255,255,255,0.92)',
            fontSize: 12,
            fontWeight: 700,
            background: '#111821',
            borderRadius: 16,
            padding: '6px 10px',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 10px 28px rgba(0,0,0,0.32)',
            whiteSpace: 'nowrap',
          }}
        >
          {creditsRemaining.toLocaleString()} credits
        </div>
      )}
      <Dropdown
        menu={{ items, theme: 'dark', style: { minWidth: 220 } }}
        trigger={['click']}
        placement="bottomRight"
        overlayStyle={{ zIndex: 3000 }}
        overlayClassName="user-menu-dropdown"
      >
        <div
          className="user-menu-trigger"
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: '#111821',
            borderRadius: 20,
            padding: '4px 10px 4px 4px',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 10px 28px rgba(0,0,0,0.32)',
          }}
        >
          <Avatar size={28} icon={<UserOutlined />} style={{ backgroundColor: roleColor[role] === 'red' ? '#f5222d' : '#1668dc' }} />
          <Text style={{ color: '#fff', fontSize: 13 }}>{displayName}</Text>
        </div>
      </Dropdown>
    </div>
  );
};

export default UserMenu;
